import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Star, Heart, Truck, ShieldCheck, Minus, Plus, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { ProductCard } from "@/components/store/ProductCard";
import { pushRecent } from "@/lib/recentlyViewed";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";

export default function ProductDetail() {
  const { slug } = useParams();
  const { format } = useCurrency();
  const { add } = useCart();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [img, setImg] = useState(0);
  const [color, setColor] = useState(null);
  const [size, setSize] = useState(null);
  const [qty, setQty] = useState(1);
  const [reviewForm, setReviewForm] = useState({ rating: 5, title: "", comment: "", author: "" });

  const load = () => api.get(`/products/${slug}`).then((r) => {
    setData(r.data);
    pushRecent(r.data.product);
    const vs = r.data.product.variants || [];
    if (vs.length) { setColor(vs[0].color || null); setSize(vs[0].size || null); }
  });

  useEffect(() => { setData(null); setImg(0); load(); /* eslint-disable-next-line */ }, [slug]);

  if (!data) return <div className="max-w-[1400px] mx-auto px-5 py-20"><div className="h-96 bg-zinc-100 rounded-xl animate-pulse" /></div>;

  const p = data.product;
  const variants = p.variants || [];
  const colors = [...new Set(variants.map((v) => v.color).filter(Boolean))];
  const sizes = [...new Set(variants.map((v) => v.size).filter((s) => s && s !== "One Size"))];
  const selectedVariant = variants.find((v) => (!colors.length || v.color === color) && (!sizes.length || v.size === size)) || variants[0];
  const price = selectedVariant?.selling_price ?? p.selling_price;
  const stock = selectedVariant?.stock ?? p.stock;
  const out = stock <= 0;

  const addToCart = () => {
    if (out) return;
    add(p, variants.length ? selectedVariant : null, qty);
  };

  const toggleWish = async () => {
    if (!user) { toast.error("Please sign in to save items"); return; }
    try { await api.post("/wishlist", { product_id: p.id }); toast.success("Wishlist updated"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    try {
      await api.post("/reviews", { product_id: p.id, ...reviewForm, author: reviewForm.author || user?.name || "Guest" });
      toast.success("Review posted");
      setReviewForm({ rating: 5, title: "", comment: "", author: "" });
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-8">
      <nav className="flex items-center gap-1.5 text-xs text-zinc-500 mb-6">
        <Link to="/" className="hover:text-zinc-900">Home</Link><ChevronRight size={12} />
        <Link to={`/shop?category=${p.category}`} className="hover:text-zinc-900 capitalize">{p.category}</Link><ChevronRight size={12} />
        <span className="text-zinc-900">{p.title}</span>
      </nav>

      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
        <div>
          <div className="aspect-square bg-zinc-100 rounded-xl overflow-hidden">
            <img src={(p.images || [])[img]} alt={p.title} className="w-full h-full object-cover" data-testid="product-main-image" />
          </div>
          {(p.images || []).length > 1 && (
            <div className="flex gap-3 mt-4">
              {p.images.map((im, i) => (
                <button key={i} onClick={() => setImg(i)} className={`w-20 h-20 rounded-lg overflow-hidden border-2 ${img === i ? "border-zinc-900" : "border-transparent"}`}>
                  <img src={im} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#FF6A1A] font-semibold mb-2">{p.brand || p.category}</p>
          <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight">{p.title}</h1>
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <div className="flex items-center gap-0.5 bg-[#FFF3E6] px-1.5 py-0.5 rounded">
              {[1, 2, 3, 4, 5].map((s) => <Star key={s} size={13} className={s <= Math.round(p.rating) ? "fill-[#FFA200] text-[#FFA200]" : "text-zinc-300"} />)}
              <span className="text-xs font-semibold ml-1">{p.rating || 0}</span>
            </div>
            <span className="text-xs text-zinc-500">{p.review_count || 0} reviews</span>
            {p.sold_count > 0 && <span className="text-xs text-zinc-500">· {p.sold_count.toLocaleString()} sold</span>}
          </div>
          <div className="flex items-center gap-3 mt-5 bg-[#FDECEE] rounded-lg p-4">
            <span className="text-3xl font-bold text-[#E23744]" data-testid="detail-price">{format(price)}</span>
            {p.compare_at_price > price && <span className="text-lg text-zinc-400 line-through">{format(p.compare_at_price)}</span>}
            {p.compare_at_price > price && <span className="bg-[#E23744] text-white text-xs font-bold px-2 py-1 rounded">-{Math.round((1 - price / p.compare_at_price) * 100)}%</span>}
          </div>

          <p className="text-sm text-zinc-600 leading-relaxed mt-5">{p.description}</p>

          {colors.length > 0 && (
            <div className="mt-6">
              <p className="text-sm font-semibold mb-2">Color: <span className="font-normal text-zinc-500">{color}</span></p>
              <div className="flex gap-2 flex-wrap">
                {colors.map((c) => (
                  <button key={c} onClick={() => setColor(c)} className={`px-4 py-2 text-sm rounded-full border transition-colors ${color === c ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-300 hover:border-zinc-900"}`} data-testid={`color-${c}`}>{c}</button>
                ))}
              </div>
            </div>
          )}
          {sizes.length > 0 && (
            <div className="mt-5">
              <p className="text-sm font-semibold mb-2">Size</p>
              <div className="flex gap-2 flex-wrap">
                {sizes.map((s) => (
                  <button key={s} onClick={() => setSize(s)} className={`w-12 h-11 text-sm rounded-lg border transition-colors ${size === s ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-300 hover:border-zinc-900"}`} data-testid={`size-${s}`}>{s}</button>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center gap-4 mt-8">
            <div className="flex items-center border border-zinc-300 rounded-full">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-3" data-testid="qty-minus"><Minus size={15} /></button>
              <span className="w-10 text-center text-sm font-medium" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(Math.min(stock, qty + 1))} className="p-3" data-testid="qty-plus"><Plus size={15} /></button>
            </div>
            <Button onClick={addToCart} disabled={out} className="flex-1 rounded-full h-12 text-sm bg-[#FF6A1A] hover:bg-[#e85f12]" data-testid="add-to-cart-btn">
              {out ? "Sold Out" : "Add to Cart"}
            </Button>
            <Button variant="outline" onClick={toggleWish} className="rounded-full h-12 w-12 p-0" data-testid="add-wishlist-btn"><Heart size={18} /></Button>
          </div>
          <p className={`text-xs mt-3 ${out ? "text-red-600" : "text-green-600"}`} data-testid="stock-status">{out ? "Out of stock" : `${stock} in stock · ships in ${p.delivery_days} days`}</p>

          <div className="mt-6 space-y-2 text-sm text-zinc-600 border-t border-zinc-200 pt-5">
            <p className="flex items-center gap-2"><Truck size={15} /> Free shipping on orders over $50</p>
            <p className="flex items-center gap-2"><ShieldCheck size={15} /> Secure checkout · 7-day returns</p>
            <p className="font-mono text-xs text-zinc-400">SKU: {p.internal_sku}</p>
          </div>
        </div>
      </div>

      {/* Reviews */}
      <div className="mt-20 grid lg:grid-cols-2 gap-16">
        <div>
          <h2 className="font-display text-2xl font-bold mb-6">Reviews ({data.reviews.length})</h2>
          <div className="space-y-5">
            {data.reviews.length === 0 && <p className="text-sm text-zinc-500">No reviews yet. Be the first!</p>}
            {data.reviews.map((r) => (
              <div key={r.id} className="border-b border-zinc-100 pb-5" data-testid={`review-${r.id}`}>
                <div className="flex items-center gap-2 mb-1">
                  <div className="flex">{[1, 2, 3, 4, 5].map((s) => <Star key={s} size={13} className={s <= r.rating ? "fill-zinc-900 text-zinc-900" : "text-zinc-300"} />)}</div>
                  <span className="text-sm font-semibold">{r.title}</span>
                </div>
                <p className="text-sm text-zinc-600">{r.comment}</p>
                <p className="text-xs text-zinc-400 mt-1">— {r.author}</p>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h2 className="font-display text-2xl font-bold mb-6">Write a Review</h2>
          <form onSubmit={submitReview} className="space-y-4">
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((s) => (
                <button type="button" key={s} onClick={() => setReviewForm({ ...reviewForm, rating: s })} data-testid={`star-${s}`}>
                  <Star size={24} className={s <= reviewForm.rating ? "fill-zinc-900 text-zinc-900" : "text-zinc-300"} />
                </button>
              ))}
            </div>
            <Input placeholder="Review title" value={reviewForm.title} onChange={(e) => setReviewForm({ ...reviewForm, title: e.target.value })} data-testid="review-title" />
            <Textarea placeholder="Share your thoughts..." value={reviewForm.comment} onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })} required data-testid="review-comment" />
            {!user && <Input placeholder="Your name" value={reviewForm.author} onChange={(e) => setReviewForm({ ...reviewForm, author: e.target.value })} data-testid="review-author" />}
            <Button type="submit" className="rounded-full" data-testid="submit-review-btn">Post Review</Button>
          </form>
        </div>
      </div>

      {data.related.length > 0 && (
        <div className="mt-20">
          <h2 className="font-display text-2xl lg:text-3xl font-bold tracking-tight mb-8">You may also like</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-8">
            {data.related.map((rp, i) => <ProductCard key={rp.id} product={rp} index={i} />)}
          </div>
        </div>
      )}
    </div>
  );
}
